import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { map, Observable, of, skipWhile, Subscription, switchMap, tap } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MdmConfig, MdmFieldMapping, MdmFieldSearchOnly } from '@parashift/shared/models';
import { CheckboxFieldComponent, InputFieldComponent, RadioFieldComponent, SelectFieldComponent, TypeaheadFieldComponent } from '@parashift/shared/components';
import { SelectOption } from '@parashift/shared/utils';
import { SqliteInitService } from './../../../master-data-management/services/sqlite-init.service';
import { MdmConfigService } from './../../services/mdm-config.service';
import { DocumentTypeFieldConfig } from './../../tabs/tab-fields/document-type-config';
import { emptyFieldMappingValidator } from './empty-field-mapping.validator';

@Component({
  selector: 'add-mdm-config-modal',
  templateUrl: './add-mdm-config-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CheckboxFieldComponent,
    FaIconComponent,
    FormsModule,
    InputFieldComponent,
    RadioFieldComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    TypeaheadFieldComponent
],
  providers: [MdmConfigService]
})
export class AddMdmConfigModalComponent implements OnInit {
  bsModalRef = inject(BsModalRef);
  private formBuilder = inject(FormBuilder);
  private sqliteInitService = inject(SqliteInitService);
  private mdmConfigService = inject(MdmConfigService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject<DestroyRef>(DestroyRef);

  state: boolean;
  model: MdmConfig;
  fields: DocumentTypeFieldConfig[];
  form: FormGroup;
  tableNameOptions: SelectOption[];
  tableColumns = [];
  documentTypeFieldOptions = [];

  private fieldMappingState: MdmFieldMapping & MdmFieldSearchOnly = {};
  private fieldMappingChangeListener: Subscription;

  ngOnInit() {
    this.sqliteInitService.init();
    this.sqliteInitService.databaseEstablished$
      .pipe(
        skipWhile(state => state !== true),
        switchMap(() => this.setTableOptions()),
        tap(() => {
          this.initDocumentTypeFieldOptions();
          this.initForm();
          this.initSearchModeChangeListener();
          this.initTableChangeListener();
          this.cdr.detectChanges();
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
  }

  searchFields = (formControl: FormControl) => {
    const term = '' + formControl.value;
    return term
      ? of(this.documentTypeFieldOptions.filter(option => option.label.includes(term)))
      : of(this.documentTypeFieldOptions);
  }

  translateInitialFieldValueToLabel = (valueProp: any): Observable<any> => {
    const option = this.documentTypeFieldOptions.find(option => option.value === valueProp);
    return of(option || {});
  }

  confirm() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.valid) {
      this.state = true;
      this.bsModalRef.content.model.name = this.form.get('name').value;
      this.bsModalRef.content.model.search_mode = this.form.get('search_mode').value;
      this.bsModalRef.content.model.table_name = this.form.get('table_name').value;

      this.bsModalRef.content.model.field_mapping = {};
      Object.keys(this.form.get('field_mapping').value)
        .filter(key => !key.includes('search_only_'))
        .forEach(key => this.bsModalRef.content.model.field_mapping[key] = this.form.get('field_mapping').value[key]);

      if (this.form.get('search_mode').value === 'advanced') {
        this.bsModalRef.content.model.search_only = {};
        Object.keys(this.form.get('field_mapping').value)
          .filter(key => key.includes('search_only_'))
          .forEach(key => this.bsModalRef.content.model.search_only[key.replace('search_only_', '')] = this.form.get('field_mapping').value[key]);
      } else {
        this.bsModalRef.content.model.search_only = {};
      }

      this.form.reset();
      this.bsModalRef.hide();
    } else {
      return;
    }
  }

  cancel() {
    this.state = false;
    this.bsModalRef.hide();
  }

  private setTableOptions() {
    return this.mdmConfigService.getTableNames().pipe(
      map(result => {
        const options = result.rows.map(row => ({ label: row['name'], value: row['name'] }));
        options.unshift({ label: '', value: '' });
        return options;
      }),
      tap(result => this.tableNameOptions = result)
    );
  }

  private initDocumentTypeFieldOptions() {
    if (this.fields.length > 0) {
      this.documentTypeFieldOptions = [];
      this.fields.forEach(field => this.documentTypeFieldOptions.push({ label: field.title + ' | ' + field.identifier + ' | ' + field.output_data_type, value: field.id }));
    }
  }

  private initForm() {
    this.form = this.formBuilder.group({
      search_mode: [this.model.search_mode ?? 'simple'],
      name: [this.model.name ?? '', Validators.required],
      table_name: [this.model.table_name ?? '', Validators.required]
    });

    if (this.model.field_mapping) {
      this.mdmConfigService.getTableColumns(this.model.table_name)
        .pipe(map(result => result.rows))
        .subscribe(result => {
          if (this.form.get('field_mapping')) {
            this.form.removeControl('field_mapping');
          }

          this.tableColumns = result;
          this.model.search_only = !this.model.search_only ? {}: this.model.search_only;
          const fieldMapping = this.formBuilder.group({});
          this.form.addControl('field_mapping', fieldMapping);

          if (this.form.get('search_mode').value === 'simple') {
            this.tableColumns.forEach(column => fieldMapping.addControl(column.name, this.formBuilder.control(this.model.field_mapping[column.name])));
          } else {
            this.tableColumns.forEach(column => {
              fieldMapping.addControl(column.name, this.formBuilder.control(this.model.field_mapping[column.name]));
              fieldMapping.addControl('search_only_' + column.name, this.formBuilder.control(this.model.search_only?.[column.name] ?? false));
            });
            this.fieldMappingState = { ...this.form.get('field_mapping').value };
            this.initFieldMappingChangeListener();
          }

          this.form.get('field_mapping').addValidators(emptyFieldMappingValidator);
          this.form.get('field_mapping').updateValueAndValidity();
          this.cdr.detectChanges();
        });
    }
  }

  private initSearchModeChangeListener() {
    this.form.get('search_mode').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((mode: string) => {
        const fieldMapping = this.form.get('field_mapping') as FormGroup;

        if (mode === 'simple') {
          this.fieldMappingChangeListener.unsubscribe();
          this.tableColumns.forEach(column => {
            fieldMapping?.removeControl('search_only_' + column.name)
          });

        } else {
          this.tableColumns.forEach(column => {
            fieldMapping?.addControl('search_only_' + column.name, this.formBuilder.control(this.model.search_only?.[column.name] ?? false));
          });
          this.fieldMappingState = { ...this.form.get('field_mapping').value };
          this.initFieldMappingChangeListener();
        }
      });
  }

  private initTableChangeListener() {
    this.form.get('table_name').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name: string) => {
        this.mdmConfigService.getTableColumns(name)
          .pipe(map(result => result.rows))
          .subscribe(result => {
            if (this.form.get('field_mapping')) {
              this.form.removeControl('field_mapping');
              this.form.get('search_mode').value === 'advanced' ? this.fieldMappingState = {} : '';
            }

            this.tableColumns = result;
            const fieldMapping = this.formBuilder.group({});
            this.form.addControl('field_mapping', fieldMapping);

            if (this.form.get('search_mode').value === 'simple') {
              this.tableColumns.forEach(column => fieldMapping.addControl(column.name, this.formBuilder.control('')));
            } else {
              this.tableColumns.forEach(column => {
                fieldMapping.addControl(column.name, this.formBuilder.control(''))
                fieldMapping.addControl('search_only_' + column.name, this.formBuilder.control(false))
              });
              this.fieldMappingChangeListener.unsubscribe();
              this.fieldMappingState = { ...this.form.get('field_mapping').value };
              this.initFieldMappingChangeListener();
            }

            this.form.get('field_mapping').addValidators(emptyFieldMappingValidator);
            this.form.get('field_mapping').updateValueAndValidity();
            this.cdr.detectChanges();
          }
        );
      });
  }

  private initFieldMappingChangeListener() {
    this.fieldMappingChangeListener = this.form.get('field_mapping').valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fieldMapping => {
        const changedField = Object.keys(fieldMapping).find(key => fieldMapping[key] !== this.fieldMappingState[key]);

        if (
          changedField
          && changedField.includes('search_only_')
          && this.form.get('field_mapping').get(changedField).value
          && this.form.get('field_mapping').get(changedField.replace('search_only_', '')).value
        ) {
          this.form.get('field_mapping').get(changedField.replace('search_only_', '')).setValue('', { emitModelToViewChange: true, emitEvent: true });
        } else if (
          changedField
          && !changedField.includes('search_only_')
          && this.form.get('field_mapping').get(changedField).value
          && this.form.get('field_mapping').get('search_only_' + changedField).value
        ) {
          this.form.get('field_mapping').get('search_only_' + changedField).setValue(false, { emitModelToViewChange: true, emitEvent: true });
        }
        this.fieldMappingState = { ...this.form.get('field_mapping').value };
      })
  }
}
